/**
 * ContextPanel — collapsible, READ-ONLY reference for the session.
 * Fetches GET /api/ccs/sessions/{id}/context and renders offer summary,
 * psychology map, winning ads and historical notes. It never writes into
 * any field. If context fails to load the panel says so and the workflow
 * continues unaffected.
 */
import React, { useEffect, useState } from 'react';

function renderValue(value) {
  if (value === null || value === undefined || value === '') {
    return <span className="text-slate-600 italic">—</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-slate-600 italic">—</span>;
    return (
      <ul className="space-y-1.5">
        {value.map((item, i) => (
          <li key={i} className="bg-slate-800/50 rounded px-2.5 py-1.5 text-xs text-slate-300">
            {typeof item === 'object' ? renderValue(item) : String(item)}
          </li>
        ))}
      </ul>
    );
  }
  if (typeof value === 'object') {
    return (
      <div className="space-y-1">
        {Object.entries(value).map(([k, v]) => (
          <div key={k} className="text-xs">
            <span className="text-slate-500">{k.replace(/_/g, ' ')}: </span>
            <span className="text-slate-300">
              {typeof v === 'object' ? renderValue(v) : String(v)}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return <span className="text-slate-300 whitespace-pre-wrap">{String(value)}</span>;
}

function Section({ title, value }) {
  return (
    <div>
      <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1.5">{title}</h4>
      <div className="text-sm">{renderValue(value)}</div>
    </div>
  );
}

export default function ContextPanel({ sessionId, api }) {
  const [open, setOpen] = useState(true);
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    api.getContext(sessionId)
      .then((d) => { if (alive) setCtx(d); })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [sessionId, api]);

  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl" data-testid="ccs-context-panel">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="ccs-context-toggle"
      >
        <span className="text-sm font-semibold text-white">Reference (read-only)</span>
        <span className="text-slate-500 text-xs">{open ? 'Hide' : 'Show'}</span>
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-slate-800/70 pt-4">
          {loading ? (
            <div className="space-y-2" data-testid="ccs-context-loading">
              <div className="h-3 bg-slate-800 rounded animate-pulse w-1/3" />
              <div className="h-3 bg-slate-800 rounded animate-pulse w-2/3" />
              <div className="h-3 bg-slate-800 rounded animate-pulse w-1/2" />
            </div>
          ) : error ? (
            <p className="text-xs text-amber-400" data-testid="ccs-context-error">
              Reference unavailable — you can keep working; this panel is optional.
            </p>
          ) : (
            <div className="space-y-4" data-testid="ccs-context-body">
              <Section title="Offer summary" value={ctx?.offer_summary} />
              <Section title="Psychology map" value={ctx?.psychology_map} />
              <Section title="Winning ads" value={ctx?.winning_ads} />
              <Section title="Historical" value={ctx?.historical} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
